<template>
  <div v-if="journey" class="deal-journey" :class="`deal-journey--${journey.status}`">
    <div class="deal-journey__caption">{{ $t('journey.card_title') }}</div>

    <div class="deal-journey__phases">
      <component
        :is="phase.key === 'sold' && salesOrderId ? 'button' : 'div'"
        v-for="(phase, index) in visiblePhases"
        :key="phase.key"
        class="deal-journey__phase"
        :class="[
          `deal-journey__phase--${phase.state}`,
          { 'deal-journey__phase--button': phase.key === 'sold' && salesOrderId },
        ]"
        :type="phase.key === 'sold' && salesOrderId ? 'button' : undefined"
        @click="openSold(phase)"
      >
        <span class="deal-journey__marker">
          <BaseIcon v-if="phase.state === 'done'" name="check" size="12" />
          <template v-else>{{ index + 1 }}</template>
        </span>
        <span class="deal-journey__phase-title">{{ $t(`journey.phases.${phase.key}`) }}</span>
        <span class="deal-journey__phase-detail">{{ phaseDetail(phase) }}</span>
      </component>
    </div>

    <div class="deal-journey__compact">
      {{ $t('journey.phase_of', { current: currentIndex + 1, total: visiblePhases.length }) }}
      · {{ $t(`journey.phases.${journey.phase}`) }}
    </div>

    <div class="deal-journey__next">
      <template v-if="journey.ball">
        <span class="deal-journey__ball" :class="`deal-journey__ball--${journey.ball}`">
          <BaseIcon :name="ballIcon" size="12" />
          {{ $t(`journey.ball.${journey.ball}`) }}
        </span>
        <span class="deal-journey__step">
          {{ stepLabel }}
        </span>
        <span v-if="waitingDays > 0" class="deal-journey__since">
          {{ $t('journey.since_days', { count: waitingDays }) }}
        </span>
      </template>
      <span v-else class="deal-journey__step deal-journey__step--final">
        {{ $t(`journey.status.${journey.status}`) }}
      </span>

      <Btn
        v-if="action"
        class="deal-journey__action"
        variant="secondary"
        size="sm"
        icon="arrow-right"
        @click="handleAction"
      >
        {{ action.label }}
      </Btn>
    </div>
  </div>
</template>

<script>
  import { ref, computed, watch, getCurrentInstance } from 'vue';
  import { dealService } from '@/services/dealService';
  import { JOURNEY_BALL_ICONS, journeyStepLabel, journeyWaitingDays } from '@/utils/journey';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Btn from '@/components/partials/Btn.vue';

  const ENTITY_ROUTES = {
    proposal: 'ProposalDetail',
    project: 'ProjectDetail',
    order: 'OrderDetail',
    work_order: 'WorkOrderDetail',
    invoice: 'InvoiceDetail',
  };

  export default {
    name: 'DealJourneyBar',

    components: { BaseIcon, Btn },

    props: {
      dealId: { type: String, required: true },
      // Changes to this value trigger a refetch (e.g. after a deal or proposal update)
      refreshKey: { type: [String, Number], default: null },
      // Hide the action when it points to the page the bar is shown on
      contextProjectId: { type: String, default: null },
    },

    emits: ['navigate', 'deal-action', 'open-sales-order'],

    setup(props, { emit, expose }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const journey = ref(null);

      const fetchJourney = async () => {
        if (!props.dealId) return;
        try {
          journey.value = await dealService.getDealJourney(props.dealId);
        } catch (error) {
          journey.value = null;
        }
      };

      watch(() => [props.dealId, props.refreshKey], fetchJourney, { immediate: true });

      // Skipped phases stay in the list: dropping them would renumber the steps
      // mid-journey and hide that the deal took a shortcut.
      const visiblePhases = computed(() => journey.value?.phases || []);

      const currentIndex = computed(() =>
        Math.max(
          0,
          visiblePhases.value.findIndex((p) => p.key === journey.value.phase)
        )
      );

      const stepLabel = computed(() => journeyStepLabel($t, journey.value?.next_step));

      const ballIcon = computed(() => JOURNEY_BALL_ICONS[journey.value?.ball] || 'user');

      const waitingDays = computed(() => journeyWaitingDays(journey.value?.waiting_since));

      const progressLabel = computed(() => {
        const j = journey.value;
        if (!j) return null;
        const { orders, work_orders: workOrders } = j.progress || {};
        if (j.phase === 'preparation' && orders?.total) {
          return $t('journey.progress.orders', { done: orders.done, total: orders.total });
        }
        if (j.phase === 'execution' && workOrders?.total) {
          return $t('journey.progress.work_orders', {
            done: workOrders.done,
            total: workOrders.total,
          });
        }
        return null;
      });

      // The current phase shows live progress when the API provides it; the other
      // phases explain what still has to happen there.
      const phaseDetail = (phase) => {
        if (phase.state === 'skipped') return $t('journey.phase_skipped');
        if (phase.state === 'current' && progressLabel.value) return progressLabel.value;
        return $t(`journey.phase_hints.${phase.key}`);
      };

      const salesOrderId = computed(() => journey.value?.sales_order?.id || null);

      const openSold = (phase) => {
        if (phase.key !== 'sold' || !salesOrderId.value) return;
        emit('open-sales-order', salesOrderId.value);
      };

      const action = computed(() => {
        const step = journey.value?.next_step;
        if (!step?.entity || !step.entity_id) return null;

        if (step.entity === 'deal') {
          const label = $t(`journey.deal_actions.${step.key}`);
          return { type: 'deal', label };
        }

        if (step.entity === 'project' && step.entity_id === props.contextProjectId) return null;

        const routeName = ENTITY_ROUTES[step.entity];
        if (!routeName) return null;
        return {
          type: 'route',
          label: $t(`journey.open.${step.entity}`),
          route: { name: routeName, params: { id: step.entity_id } },
        };
      });

      const handleAction = () => {
        if (!action.value) return;
        if (action.value.type === 'deal') {
          emit('deal-action', journey.value.next_step);
        } else {
          emit('navigate', action.value.route);
        }
      };

      expose({ refresh: fetchJourney });

      return {
        journey,
        visiblePhases,
        currentIndex,
        stepLabel,
        ballIcon,
        waitingDays,
        progressLabel,
        phaseDetail,
        action,
        salesOrderId,
        openSold,
        handleAction,
      };
    },
  };
</script>

<style scoped lang="scss">
  .deal-journey {
    border: 1px solid var(--slate-300);
    border-radius: 4px;
    padding: 1rem;
    background: white;

    &__caption {
      color: var(--slate-600);
      font-size: 10px;
      font-weight: 500;
      line-height: 20px;
      text-transform: uppercase;
      letter-spacing: 0.02em;

      @include lg-down {
        display: none;
      }
    }

    &__phases {
      display: flex;
      align-items: flex-start;
      gap: 1rem;
      margin-top: 0.75rem;

      @include lg-down {
        display: none;
      }
    }

    &__phase {
      display: flex;
      flex: 1;
      flex-direction: column;
      gap: 0.5rem;
      min-width: 0;

      &--button {
        padding: 0;
        border: 0;
        background: none;
        text-align: left;
        cursor: pointer;
        font: inherit;

        &:hover .deal-journey__phase-title {
          text-decoration: underline;
        }
      }
    }

    &__marker {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      flex-shrink: 0;
      border: 1px solid var(--slate-300);
      border-radius: 50%;
      background: white;
      color: var(--slate-600);
      font-size: 11px;
      font-weight: 400;
      line-height: 20px;

      .deal-journey__phase--done &,
      .deal-journey__phase--current & {
        background: var(--brand-600);
        border-color: var(--brand-600);
        color: white;
      }

      .deal-journey__phase--current & {
        box-shadow: 0 0 0 3px var(--brand-100);
      }

      .deal-journey__phase--skipped & {
        border-style: dashed;
        color: var(--slate-400);
      }
    }

    &__phase-title {
      color: var(--slate-600);
      font-size: 13px;
      font-weight: 500;
      line-height: 20px;
      @include ellipsis(1);

      .deal-journey__phase--current & {
        color: var(--slate-900);
      }

      .deal-journey__phase--skipped & {
        color: var(--slate-400);
        text-decoration: line-through;
      }
    }

    &__phase-detail {
      color: var(--slate-500);
      font-size: 11px;
      line-height: 16px;
      // Two lines: with seven phases a single line truncates nearly every hint
      @include ellipsis(2);

      .deal-journey__phase--skipped & {
        color: var(--slate-400);
      }
    }

    &__compact {
      display: none;
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--brand-600);

      @include lg-down {
        display: block;
      }
    }

    &__next {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin-top: 0.75rem;
      padding-top: 0.75rem;
      border-top: 1px solid var(--slate-200);
    }

    &__ball {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.125rem 0.5rem;
      border-radius: 9999px;
      font-size: 0.6875rem;
      font-weight: 600;
      white-space: nowrap;

      &--us {
        background: var(--amber-100);
        color: var(--amber-800);
      }

      &--customer {
        background: var(--blue-100);
        color: var(--blue-700);
      }

      &--supplier {
        background: var(--indigo-100);
        color: var(--indigo-600);
      }

      &--installer {
        background: var(--emerald-100);
        color: var(--emerald-600);
      }
    }

    &__step {
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--slate-900);

      &--final {
        color: var(--slate-600);
      }
    }

    &__since {
      font-size: 0.75rem;
      color: var(--slate-500);
    }

    &__action {
      margin-left: auto;
    }

    &--lost {
      .deal-journey__marker {
        filter: grayscale(1);
        opacity: 0.6;
      }
    }
  }
</style>
